import React, { useEffect, useState } from 'react';
import { Box, Skeleton } from '@ace-clublabs/shared-component-library';
import { IGetManifest, PaymentType } from '@ace-clublabs/payment-types';
import { IGetAdyenSession } from '../api/contracts';
import { LogCannon } from '@ace-clublabs/log-cannon';
import EmbedAdyenPayment from '../components/EmbedAdyenPayment/EmbedAdyenPayment';
import { isParentMessage, isOriginAllowed, setParentOrigin } from '../components/EmbedAdyenPayment/embedPostMessage';
import '../components/EmbedAdyenPayment/EmbedAdyenPayment.scss';

const CARD_TYPES: PaymentType[] = [PaymentType.CreditOrDebit, PaymentType.DebitOnly, PaymentType.Prepaid];
const WALLET_TYPES: PaymentType[] = [PaymentType.Apple, PaymentType.Google];

interface Props {
  manifestId: string;
  manifest: IGetManifest;
  adyenSession: IGetAdyenSession;
}

const EmbedAdyenPaymentRouter: React.FC<Props> = ({ manifestId, manifest, adyenSession }) => {
  const [parentReady, setParentReady] = useState(false);

  const allowedPaymentTypes = manifest?.clientConfig?.allowedPaymentTypes || [];
  const hasCardType = allowedPaymentTypes.some((t: PaymentType) => CARD_TYPES.includes(t));
  const hasWalletType = allowedPaymentTypes.some((t: PaymentType) => WALLET_TYPES.includes(t));
  const showSelectorSkeleton = hasCardType && hasWalletType && !manifest?.paymentType;

  let applePayAvailable = false;
  try {
    applePayAvailable = !!(window as any).ApplePaySession?.canMakePayments?.();
  } catch (e) {
    LogCannon.blast('ApplePaySession.canMakePayments threw (insecure context)', { error: e });
  }
  const googlePayAvailable = !!(window as any).google?.payments?.api?.PaymentsClient;
  const skeletonRowCount =
    (hasCardType ? 1 : 0) +
    (allowedPaymentTypes.includes(PaymentType.Apple) && applePayAvailable ? 1 : 0) +
    (allowedPaymentTypes.includes(PaymentType.Google) && googlePayAvailable ? 1 : 0);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isOriginAllowed(event.origin)) return;
      if (!isParentMessage(event.data)) return;

      setParentOrigin(event.origin);

      if (event.data.type === 'START') {
        setParentReady(true);
      }
    };

    window.addEventListener('message', handleMessage);

    if (window.parent !== window) {
      const referrer = document.referrer;
      if (referrer) {
        try {
          const origin = new URL(referrer).origin;
          if (isOriginAllowed(origin)) {
            setParentOrigin(origin);
          }
        } catch {
          // invalid referrer
        }
      }
      window.parent.postMessage({ source: 'ace-payments-embed', type: 'READY', payload: {} }, '*');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="embed-router-wrapper">
      <div className={`embed-router-wrapper__form${parentReady ? ' embed-router-wrapper__form--visible' : ''}`}>
        <EmbedAdyenPayment manifestId={manifestId} manifest={manifest} adyenSession={adyenSession} />
      </div>

      <div className={`embed-router-wrapper__skeleton${parentReady ? ' embed-router-wrapper__skeleton--hidden' : ''}`}>
        <div className="embed-adyen-payment">
          <Box
            className="embed-payment-info-box"
            sx={{ border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '14px', padding: '24px', mb: '30px' }}
          >
            <div className="embed-adyen-payment-skeleton">
              {showSelectorSkeleton ? (
                <>
                  <Skeleton className="embed-adyen-payment-skeleton__title" variant="rectangular" height={20} />
                  <Skeleton className="embed-adyen-payment-skeleton__subtitle" variant="rectangular" height={20} />
                  {Array.from({ length: skeletonRowCount }, (_, idx) => (
                    <Skeleton key={idx} className="embed-adyen-payment-skeleton__selector-row" variant="rectangular" height={56} />
                  ))}
                </>
              ) : (
                <>
                  <Skeleton className="embed-adyen-payment-skeleton__title" variant="rectangular" height={32} />
                  <Skeleton className="embed-adyen-payment-skeleton__subtitle" variant="rectangular" height={20} />
                  <Skeleton className="embed-adyen-payment-skeleton__label" variant="rectangular" height={20} />
                  <Skeleton className="embed-adyen-payment-skeleton__field--full" variant="rectangular" height={38} />
                  <div className="embed-adyen-payment-skeleton__row">
                    <Skeleton className="embed-adyen-payment-skeleton__label--third" variant="rectangular" height={20} />
                    <Skeleton className="embed-adyen-payment-skeleton__label--third" variant="rectangular" height={20} />
                    <Skeleton className="embed-adyen-payment-skeleton__label--third" variant="rectangular" height={20} />
                  </div>
                  <div className="embed-adyen-payment-skeleton__row">
                    <Skeleton className="embed-adyen-payment-skeleton__field--third" variant="rectangular" height={38} />
                    <Skeleton className="embed-adyen-payment-skeleton__field--third" variant="rectangular" height={38} />
                    <Skeleton className="embed-adyen-payment-skeleton__field--third" variant="rectangular" height={38} />
                  </div>
                  <Skeleton className="embed-adyen-payment-skeleton__label" variant="rectangular" height={20} />
                  <Skeleton className="embed-adyen-payment-skeleton__field--full" variant="rectangular" height={38} />
                </>
              )}
            </div>
          </Box>
          <Skeleton className="embed-adyen-payment-skeleton__button" variant="rectangular" height={48} />
        </div>
      </div>
    </div>
  );
};

export default EmbedAdyenPaymentRouter;